/**
 * THE PICKERS THE SCREENS ASK FOR.
 *
 * `Picker` in ui.tsx is the mechanism. These are the records people choose
 * often enough that every screen should spell them the same way, and they
 * exist so swapping a capped <select> for a real search is one line rather
 * than thirteen.
 *
 * Customers matter most: a gas company has more of them than any other kind
 * of record, and the old control asked for the API's own maximum, so customer
 * two hundred and one could not be chosen at all — on thirteen screens. SKUs
 * are a gas times a grade and will not reach two hundred for a long time, so
 * those keep their select; where one is truncated it now says so rather than
 * looking complete.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api } from './api.js';
import { Picker } from './ui.js';

export interface PickedCustomer { customerId: number; name: string }
export interface PickedSupplier { supplierId: number; name: string }

export function CustomerPicker({ name, label, value, onChange, placeholder, help, activeOnly = true }: {
  name: string; label: string; value: PickedCustomer | null;
  onChange: (c: PickedCustomer | null) => void;
  placeholder?: string; help?: string; activeOnly?: boolean;
}) {
  return (
    <Picker<PickedCustomer>
      name={name} label={label} value={value} onChange={onChange}
      placeholder={placeholder ?? 'Search or select customer…'}
      {...(help !== undefined ? { help } : {})}
      render={(c) => c.name} keyOf={(c) => c.customerId}
      search={async (q, page) => {
        const r = await api.view('customers.list', {
          ...(activeOnly ? { active: true } : {}), ...(q ? { q } : {}), page, pageSize: 25,
        });
        return { rows: r.rows.map((c) => ({ customerId: c.customerId, name: c.name })), total: r.page.total };
      }} />
  );
}

/**
 * THE SAME PICKER, FOR A SCREEN THAT KEEPS THE ID AS A STRING.
 *
 * Nearly every screen holds `const [customerId, setCustomerId] = useState('')`
 * and fed it to a <select>. Rather than rework thirteen screens' state, this
 * takes the id and resolves the NAME itself — the one piece of bookkeeping
 * that makes the swap fiddly, done once here. The id moves without the picker
 * on a route parameter, a restored draft or a cleared form, and a trigger
 * reading "Search or select customer…" while a customer IS chosen is worse
 * than either state alone.
 */
export function CustomerPickerById({ name, label, customerId, onPick, placeholder, help }: {
  name: string; label: string; customerId: string;
  onPick: (id: string, customer: PickedCustomer | null) => void;
  placeholder?: string; help?: string;
}) {
  const [chosen, setChosen] = useState<PickedCustomer | null>(null);
  useEffect(() => {
    if (!customerId) { setChosen(null); return; }
    if (chosen && String(chosen.customerId) === customerId) return;
    let live = true;
    void api.view('customers.get', { customerId: Number(customerId) }).then(
      (r) => { if (live && r.customer) setChosen({ customerId: r.customer.customerId, name: r.customer.name }); },
      () => { /* the name is a convenience; the id is what submits */ },
    );
    return () => { live = false; };
  }, [customerId]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <CustomerPicker name={name} label={label} value={chosen}
                    {...(placeholder !== undefined ? { placeholder } : {})}
                    {...(help !== undefined ? { help } : {})}
                    onChange={(c) => { setChosen(c); onPick(c ? String(c.customerId) : '', c); }} />
  );
}

export function SupplierPickerById({ name, label, supplierId, onPick, placeholder }: {
  name: string; label: string; supplierId: string;
  onPick: (id: string) => void; placeholder?: string;
}) {
  const [chosen, setChosen] = useState<PickedSupplier | null>(null);
  useEffect(() => {
    if (!supplierId) { setChosen(null); return; }
    if (chosen && String(chosen.supplierId) === supplierId) return;
    let live = true;
    void api.view('suppliers.list', { pageSize: 25 }).then(
      (r) => { const m = r.rows.find((x) => String(x.supplierId) === supplierId); if (live && m) setChosen({ supplierId: m.supplierId, name: m.name }); },
      () => { /* as above */ },
    );
    return () => { live = false; };
  }, [supplierId]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Picker<PickedSupplier>
      name={name} label={label} value={chosen} placeholder={placeholder ?? 'Search or select supplier…'}
      render={(sx) => sx.name} keyOf={(sx) => sx.supplierId}
      onChange={(sx) => { setChosen(sx); onPick(sx ? String(sx.supplierId) : ''); }}
      search={async (q, page) => {
        const r = await api.view('suppliers.list', { active: true, ...(q ? { q } : {}), page, pageSize: 25 });
        return { rows: r.rows.map((x) => ({ supplierId: x.supplierId, name: x.name })), total: r.page.total };
      }} />
  );
}

/**
 * WHAT A CAPPED LIST MUST SAY. Where a plain <select> is still right, this is
 * the line that goes under it when the server had more than it was given.
 * Silence was the bug: a list that stops at its page size and says nothing is
 * indistinguishable from a complete one.
 */
export function Truncated({ shown, total, what }: { shown: number; total: number; what: string }) {
  if (total <= shown) return null;
  return (
    <p className="muted" style={{ marginTop: 4 }}>
      Showing {shown} of {total} {what}. Type to narrow the list — the rest are not on it.
    </p>
  );
}
